import { QueryClient, useQuery } from "@tanstack/react-query";
import axiosInstance from "services/api";
import { API_URL } from "@/constants";
import type { MediaAsset } from "pages/media/types/";

const POLL_INTERVAL = 3000;
// About 2 minutes, then the video is left as "still processing"
const MAX_POLLS = 40;

const assetHasAudioQueryKey = (assetId: string | undefined) => [
  "media",
  "assets",
  assetId,
  "has_audio",
];

const fetchHasAudio = async (
  assetId: string | undefined,
  signal?: AbortSignal
): Promise<boolean | null> => {
  if (!assetId) return null;
  const { data } = await axiosInstance.get<MediaAsset>(
    `${API_URL}/media/media/assets/${assetId}`,
    { signal }
  );
  return data.has_audio ?? null;
};

/** One-off read sharing the hook's cache; an error counts as unknown (null). */
export const fetchAssetHasAudio = (
  queryClient: QueryClient,
  assetId: string
): Promise<boolean | null> =>
  queryClient
    .fetchQuery({
      queryKey: assetHasAudioQueryKey(assetId),
      queryFn: ({ signal }) => fetchHasAudio(assetId, signal),
    })
    .catch(() => null);

/**
 * Whether the video asset has a soundtrack. Polls while the backend has not
 * extracted it yet (null), e.g. right after an upload.
 */
export const useAssetHasAudio = (assetId: string | undefined) => {
  const query = useQuery({
    queryKey: assetHasAudioQueryKey(assetId),
    queryFn: ({ signal }) => fetchHasAudio(assetId, signal),
    enabled: !!assetId,
    refetchInterval: (data, query) =>
      data === null && query.state.dataUpdateCount < MAX_POLLS
        ? POLL_INTERVAL
        : false,
  });

  return {
    hasAudio: query.data,
    isLoading: query.isLoading && !!assetId,
    isError: query.isError,
  };
};
